import {type QueryClient, queryOptions} from '@github-ui/react-query'

import type {SavedViewsBoardAdapter} from '../types'
import type {IssuesBoardMetadata} from './board-metadata'
import type {BoardAxisLabels, BoardPayloadItem} from './board-payload'
import {type AssignAgentAction, type BoardCardActions, REPOSITORY_SCOPE} from './card-actions-payload'
import type {BoardDeferredFields} from './deferred-payload'

export const CARD_ACTIONS_QUERY_PREFIX = 'saved-views-board-card-actions'
/** Availability a repository decided, by view and repository, for its other cards to reuse. */
const REPOSITORY_AVAILABILITY_QUERY_PREFIX = 'saved-views-board-repository-card-actions'
/** Whether a viewer may act on an item changes with repository settings, not with the board. */
const CARD_ACTIONS_STALE_TIME_MS = 5 * 60_000

/**
 * Loads the card menu actions the initial board payload leaves out, cached by view and item ID, and
 * by repository for availability the repository decided.
 */
export interface BoardCardActionsSource {
  /** Names the view independently of the board response, so refreshed boards reuse each item's actions. */
  scope: ReadonlyArray<string | number>
  /** Resolves `undefined` for an item the viewer cannot read. */
  load: (globalId: string) => Promise<BoardCardActions | undefined>
}

export type IssuesSavedViewBoardAdapter = SavedViewsBoardAdapter<
  BoardPayloadItem,
  IssuesBoardMetadata,
  BoardAxisLabels,
  BoardDeferredFields
> & {
  /** Answers, per card, the menu actions the initial payload leaves out. */
  cardActionsSource?: BoardCardActionsSource
}

export function boardCardActionsQueryOptions(
  {globalId, repoNameWithOwner}: {globalId: string; repoNameWithOwner: string},
  {scope: viewScope, load}: BoardCardActionsSource,
  queryClient: QueryClient,
) {
  const repositoryKey = repositoryAvailabilityKey(viewScope, repoNameWithOwner)

  return queryOptions({
    queryKey: [CARD_ACTIONS_QUERY_PREFIX, ...viewScope, globalId, repoNameWithOwner],
    queryFn: async ({client}): Promise<BoardCardActions> => {
      const askedAt = Date.now()
      const actions = (await load(globalId)) ?? {}
      const availability = actions.assignAgent
      if (availability?.scope !== REPOSITORY_SCOPE || !availability.repository) return actions

      const later = publishRepositoryAvailability(client, viewScope, availability.repository, availability, askedAt)
      return later ? {...actions, assignAgent: later} : actions
    },
    // Cards ask only about issues the board shows open, which a repository's availability holds for.
    initialData: () => {
      const known = queryClient.getQueryState<AssignAgentAction>(repositoryKey)
      if (!known?.data || Date.now() - known.dataUpdatedAt >= CARD_ACTIONS_STALE_TIME_MS) return undefined
      return {assignAgent: known.data}
    },
    // Read only when `initialData` returned data. Keeps the repository availability's age, so reuse does not extend it.
    initialDataUpdatedAt: () => queryClient.getQueryState(repositoryKey)?.dataUpdatedAt,
    staleTime: CARD_ACTIONS_STALE_TIME_MS,
  })
}

function repositoryAvailabilityKey(viewScope: BoardCardActionsSource['scope'], nameWithOwner: string) {
  return [REPOSITORY_AVAILABILITY_QUERY_PREFIX, ...viewScope, nameWithOwner.toLowerCase()]
}

/**
 * Stores a repository's availability, dated when it was asked, and brings cards holding an earlier one
 * for that repository up to it, as `initialData` only seeds cards not yet asked about. Returns the
 * stored availability instead when a request asked later has already returned.
 */
function publishRepositoryAvailability(
  queryClient: QueryClient,
  viewScope: BoardCardActionsSource['scope'],
  nameWithOwner: string,
  availability: AssignAgentAction,
  askedAt: number,
): AssignAgentAction | undefined {
  const key = repositoryAvailabilityKey(viewScope, nameWithOwner)
  const stored = queryClient.getQueryState<AssignAgentAction>(key)
  if (stored?.data && stored.dataUpdatedAt > askedAt) return stored.data

  // Replaced rather than updated: nothing observes this entry, and an update does not restart its removal timer.
  queryClient.removeQueries({queryKey: key, exact: true})
  queryClient.setQueryData(key, availability, {updatedAt: askedAt})

  const repository = nameWithOwner.toLowerCase()
  queryClient.setQueriesData<BoardCardActions>(
    {
      queryKey: [CARD_ACTIONS_QUERY_PREFIX, ...viewScope],
      predicate: ({state}) => {
        const held = (state.data as BoardCardActions | undefined)?.assignAgent
        return (
          held?.scope === REPOSITORY_SCOPE &&
          held.repository?.toLowerCase() === repository &&
          state.dataUpdatedAt < askedAt
        )
      },
    },
    actions => actions && {...actions, assignAgent: availability},
    {updatedAt: askedAt},
  )
  return undefined
}
